// utils/session/sessionManager.ts
import Cookies from 'js-cookie';
import logger from '../logging/logger';
import config from '../../config';

const componentName = 'sessionManager';

// Constants
const COOKIE_DOMAIN = window.location.hostname;
const SECURE = window.location.protocol === 'https:';
const DEFAULT_EXPIRY = config.COOKIE_EXPIRY_DAYS;
const SESSION_COOKIE_PREFIX = config.COOKIE_PREFIX; 

// Cookie names
const COOKIES = {
    SESSION: `${SESSION_COOKIE_PREFIX}session`,
    LAST_ACTIVITY: `${SESSION_COOKIE_PREFIX}last_activity`,
};

// LocalStorage keys for anonymous users (persists across browser sessions)
const LOCAL_STORAGE_KEYS = {
    ANONYMOUS_USER: `${SESSION_COOKIE_PREFIX}anon_user`,
};

// Anonymous user data structure
interface AnonymousUserData {
    userId: number;
    userName: string;
    created: number; // timestamp
    expires: number; // timestamp
}

// Default cookie options
const defaultOptions = {
    path: '/',
    secure: SECURE,
    sameSite: 'strict' as const,
    domain: COOKIE_DOMAIN === 'localhost' ? undefined : COOKIE_DOMAIN,
};

// Helper function to ensure consistent key formatting
const formatCookieKey = (key: string): string => {
    // If the key already starts with the prefix, don't add it again
    return key.startsWith(SESSION_COOKIE_PREFIX) ? key : `${SESSION_COOKIE_PREFIX}${key}`;
};

// Session management functions
export const sessionManager = {
    /**
     * Initialize the user session
     */
    initSession: () => {
        // Update last activity timestamp
        sessionManager.updateActivity();

        // Set session cookie
        Cookies.set(COOKIES.SESSION, 'active', {
            ...defaultOptions,
            expires: DEFAULT_EXPIRY,
        });
    },


    /**
     * Update the last activity timestamp
     */
    updateActivity: () => {
        const now = Date.now();
        Cookies.set(COOKIES.LAST_ACTIVITY, now.toString(), {
            ...defaultOptions,
            expires: DEFAULT_EXPIRY,
        });
        logger.log('LogSession', componentName, 'Updated activity timestamp', { timestamp: now });
    },

    /**
     * Check if the session is active
     */
    isSessionActive: (): boolean => {
        const isActive = Cookies.get(COOKIES.SESSION) === 'active';
        logger.log('LogSession', componentName, 'Session active check', { isActive });
        return isActive;
    },

    /**
     * Check if the session has expired due to inactivity
     * @param maxInactivityMinutes Maximum allowed inactivity in minutes
     */
    isSessionExpired: (maxInactivityMinutes: number = 30): boolean => {
        const lastActivity = Cookies.get(COOKIES.LAST_ACTIVITY);
        if (!lastActivity) {
            logger.log('LogSession', componentName, 'Session expired: No last activity timestamp');
            return true;
        }

        const lastActivityTime = parseInt(lastActivity, 10);
        const currentTime = Date.now();
        const inactivityMs = currentTime - lastActivityTime;
        const inactivityMinutes = inactivityMs / (1000 * 60);

        const isExpired = inactivityMinutes > maxInactivityMinutes;
        logger.log('LogSession', componentName, 'Session expiry check', {
            lastActivityTime,
            currentTime,
            inactivityMinutes,
            maxInactivityMinutes,
            isExpired
        });

        return isExpired;
    },

    ///**
    // * End the user session and clear cookies
    // */
    //endSession: () => {
    //    logger.log('LogSession', componentName, 'Ending session');

    //    // Clear all sail quoter cookies
    //    Object.keys(Cookies.get() || {})
    //        .filter(name => name.startsWith(SESSION_COOKIE_PREFIX))
    //        .forEach(name => {
    //            Cookies.remove(name, { path: '/' });
    //            logger.log('LogSession', componentName, `Removed cookie: ${name}`);
    //        });
    //},


    /**
     * Enhanced session ending that clears all cookies more thoroughly
     */
    endSession: () => {
        logger.log('LogSession', componentName, 'Ending session');

        // Get all cookies first
        const allCookies = Cookies.get() || {};

        // Clear all sail quoter cookies with both sets of options
        Object.keys(allCookies)
            .filter(name => name.startsWith(SESSION_COOKIE_PREFIX))
            .forEach(name => {
                // Try clearing with full options first
                Cookies.remove(name, {
                    path: '/',
                    domain: COOKIE_DOMAIN === 'localhost' ? undefined : COOKIE_DOMAIN,
                    secure: SECURE,
                    sameSite: 'strict'
                });

                // Fallback clear with minimal options
                Cookies.remove(name, { path: '/' });

                logger.log('LogSession', componentName, `Removed cookie: ${name}`);
            });
    },

    /**
     * Store data in a cookie
     * @param key The key to store the data under
     * @param data The data to store
     * @param expiryDays Number of days until the cookie expires
     */
    storeData: <T>(key: string, data: T, expiryDays: number = DEFAULT_EXPIRY): void => {
        const cookieKey = formatCookieKey(key);
        Cookies.set(cookieKey, JSON.stringify(data), {
            ...defaultOptions,
            expires: expiryDays,
        });

        logger.log('LogSession', componentName, `Stored data in cookie: ${cookieKey}`);
    },

    /**
     * Retrieve data from a cookie
     * @param key The key the data is stored under
     * @returns The data, or null if no data exists
     */
    retrieveData: <T>(key: string): T | null => {
        const cookieKey = formatCookieKey(key);
        const cookieData = Cookies.get(cookieKey);

        if (!cookieData) {
            logger.log('LogSession', componentName, `No data found for cookie: ${cookieKey}`);
            return null;
        }

        try {
            const parsedData = JSON.parse(cookieData) as T;
            logger.log('LogSession', componentName, `Retrieved data from cookie: ${cookieKey}`);
            return parsedData;
        } catch (e) {
            console.error('LogSession', componentName, `Failed to parse cookie data for key ${cookieKey}:`, e);
            return null;
        }
    },

    /**
     * Check if a cookie exists
     * @param key The key to check for
     * @returns True if the cookie exists, false otherwise
     */
    hasData: (key: string): boolean => {
        const cookieKey = formatCookieKey(key);
        return Cookies.get(cookieKey) !== undefined;
    },

    /**
     * Clear a specific cookie
     * @param key The key to clear
     */
    /**
     * Clear a specific cookie
     * @param key The key to clear
     */
    clearData: (key: string): void => {
        const cookieKey = formatCookieKey(key);

        // Clear with the same options used when setting cookies
        Cookies.remove(cookieKey, {
            path: '/',
            domain: COOKIE_DOMAIN === 'localhost' ? undefined : COOKIE_DOMAIN,
            secure: SECURE,
            sameSite: 'strict'
        });

        // Also try clearing without domain option as fallback
        Cookies.remove(cookieKey, { path: '/' });

        logger.log('LogSession', componentName, `Cleared cookie: ${cookieKey}`);
    },

    /**
     * Store anonymous user data in localStorage (persists across browser sessions)
     * @param userId The anonymous user ID
     * @param userName The anonymous username
     * @param expiryDays Number of days until expiration (default 90 days)
     */
    storeAnonymousUser: (userId: number, userName: string, expiryDays: number = 90): void => {
        const now = Date.now();
        const anonymousData: AnonymousUserData = {
            userId,
            userName,
            created: now,
            expires: now + (expiryDays * 24 * 60 * 60 * 1000)
        };

        try {
            localStorage.setItem(
                LOCAL_STORAGE_KEYS.ANONYMOUS_USER,
                JSON.stringify(anonymousData)
            );
            logger.log('LogSession', componentName, 'Stored anonymous user in localStorage', {
                userId,
                userName,
                expiryDays
            });
        } catch (e) {
            logger.log('LogError', componentName, 'Failed to store anonymous user in localStorage', e);
        }
    },

    /**
     * Retrieve anonymous user data from localStorage
     * @returns Anonymous user data or null if not found/expired
     */
    retrieveAnonymousUser: (): AnonymousUserData | null => {
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.ANONYMOUS_USER);
            if (!stored) {
                logger.log('LogSession', componentName, 'No anonymous user found in localStorage');
                return null;
            }

            const data: AnonymousUserData = JSON.parse(stored);
            const now = Date.now();

            // Check if expired
            if (data.expires < now) {
                logger.log('LogSession', componentName, 'Anonymous user data expired', {
                    userId: data.userId,
                    expired: new Date(data.expires).toISOString()
                });
                sessionManager.clearAnonymousUser();
                return null;
            }

            logger.log('LogSession', componentName, 'Retrieved anonymous user from localStorage', {
                userId: data.userId,
                userName: data.userName,
                created: new Date(data.created).toISOString(),
                expires: new Date(data.expires).toISOString()
            });
            return data;
        } catch (e) {
            logger.log('LogError', componentName, 'Failed to retrieve anonymous user from localStorage', e);
            return null;
        }
    },

    /**
     * Clear anonymous user data from localStorage
     */
    clearAnonymousUser: (): void => {
        try {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.ANONYMOUS_USER);
            logger.log('LogSession', componentName, 'Cleared anonymous user from localStorage');
        } catch (e) {
            logger.log('LogError', componentName, 'Failed to clear anonymous user from localStorage', e);
        }
    },

    /**
     * Check if anonymous user data exists and is valid
     */
    hasAnonymousUser: (): boolean => {
        const data = sessionManager.retrieveAnonymousUser();
        return data !== null;
    },

};

export default sessionManager;